"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CalculatorService = exports.GALLON_PER_LITER = exports.LITER_PER_GALLON = exports.MILE_PER_KM = exports.KM_PER_MILE = void 0;
exports.KM_PER_MILE = 1.60934;
exports.MILE_PER_KM = 1 / exports.KM_PER_MILE;
exports.LITER_PER_GALLON = 3.785;
exports.GALLON_PER_LITER = 1 / exports.LITER_PER_GALLON;
class CalculatorService {
    getSavings(inputs, country) {
        // All calculations are based on us imperial units
        switch (country) {
            case 'US':
                this.input = inputs;
                break;
            case 'GB':
                this.input = this.convertGbToImperial(inputs);
                break;
            default:
                this.input = this.convertMetricToImperial(inputs);
                break;
        }
        const idlingReductionFactor = 0.8;
        const routeOptimizationFactor = 0.15;
        const driverBehaviorIncreaseFactor = 0.08;
        return {
            idle: this.annual_savings_from_idling_reduction(idlingReductionFactor),
            optimization: this.annual_savings_from_route_optimization(routeOptimizationFactor),
            driverBehavior: this.annual_savings_from_improved_driver_behaviour(driverBehaviorIncreaseFactor),
            total: this.total_annual_savings(idlingReductionFactor, routeOptimizationFactor, driverBehaviorIncreaseFactor),
            carbon: this.carbon_emissions_in_metric_tons(this.total_gallons_saved(idlingReductionFactor, routeOptimizationFactor, driverBehaviorIncreaseFactor)),
        };
    }
    convertMetricToImperial(input) {
        // Liter / 100KM => Gallon / Mile
        let fuelEfficiency = (input.fuelEfficiency / 100) * exports.GALLON_PER_LITER * exports.KM_PER_MILE;
        // Gallon / Mile => Mile / Gallon
        fuelEfficiency = 1 / fuelEfficiency;
        return {
            ...input,
            // Currency per Litre => Pounds per Gallon
            fuelCost: input.fuelCost * exports.LITER_PER_GALLON,
            fuelEfficiency,
            // KM => Miles
            estimatedDistance: input.estimatedDistance * exports.MILE_PER_KM,
        };
    }
    convertGbToImperial(input) {
        return {
            ...input,
            // Pounds per Litre => Pounds per Gallon
            fuelCost: input.fuelCost * exports.LITER_PER_GALLON,
            // Miles / Liter => Miles / Gallon
            fuelEfficiency: input.fuelEfficiency * exports.LITER_PER_GALLON,
            // Miles => Miles
            estimatedDistance: input.estimatedDistance,
        };
    }
    total_daily_mileage() {
        return this.input.fleetSize * this.input.estimatedDistance;
    }
    total_workdays_per_year() {
        return this.input.workDays * 52;
    }
    total_miles_per_year() {
        return this.total_daily_mileage() * this.total_workdays_per_year();
    }
    total_gallons_of_gas_per_day(mpg = 0) {
        return this.total_daily_mileage() / (mpg || this.input.fuelEfficiency);
    }
    total_gallons_of_gas_per_year(mpg = 0) {
        return (this.total_gallons_of_gas_per_day(mpg || this.input.fuelEfficiency) *
            this.total_workdays_per_year());
    }
    total_hours_idling_per_day() {
        return this.input.fleetSize * this.input.idleTime;
    }
    total_hours_idling_per_year() {
        return this.total_hours_idling_per_day() * this.total_workdays_per_year();
    }
    idling_cost(hours) {
        return 3.635 * hours;
    }
    carbon_emissions_in_pounds(gallons) {
        return 19.4 * gallons;
    }
    carbon_emissions_in_metric_tons(gallons) {
        return (gallons * 19.4) / 2204.62262;
    }
    daily_idling_cost() {
        return this.total_hours_idling_per_day() * 3.635;
    }
    daily_fuel_cost() {
        return (this.total_gallons_of_gas_per_day(this.input.fuelEfficiency) *
            this.input.fuelCost +
            this.daily_idling_cost());
    }
    annual_fuel_cost() {
        return this.daily_fuel_cost() * this.total_workdays_per_year();
    }
    annual_idling_cost() {
        return this.daily_idling_cost() * this.total_workdays_per_year();
    }
    daily_carbon_emissions() {
        return this.carbon_emissions_in_pounds(this.total_daily_mileage() / this.input.fuelEfficiency);
    }
    annual_carbon_emissions() {
        return this.daily_carbon_emissions() * this.total_workdays_per_year();
    }
    daily_carbon_emissions_in_tons() {
        return this.daily_carbon_emissions() / 2204.62262;
    }
    annual_carbon_emissions_in_tons() {
        return this.annual_carbon_emissions() / 2204.62262;
    }
    // Takes a decimal. 80% reduction/increase = 0.8, and 8% = 0.08
    gallons_saved_per_year_from_idling_reduction(reduction) {
        return this.total_hours_idling_per_year() * reduction;
    }
    mileage_saved_per_year_from_route_optimization(reduction) {
        return this.total_miles_per_year() * reduction;
    }
    gallons_saved_per_year_from_route_optimization(reduction) {
        return (this.mileage_saved_per_year_from_route_optimization(reduction) /
            this.input.fuelEfficiency);
    }
    increase_in_mpg_by(increase) {
        return this.input.fuelEfficiency * (1 + increase);
    }
    total_gallons_of_gas_saved_with_reductions(idlingReductionFactor, routeOptimizationFactor) {
        return (this.gallons_saved_per_year_from_idling_reduction(idlingReductionFactor) +
            this.gallons_saved_per_year_from_route_optimization(routeOptimizationFactor));
    }
    annual_savings_from_idling_reduction(reduction) {
        return (this.gallons_saved_per_year_from_idling_reduction(reduction) *
            this.input.fuelCost);
    }
    annual_savings_from_route_optimization(reduction) {
        return (this.gallons_saved_per_year_from_route_optimization(reduction) *
            this.input.fuelCost);
    }
    annual_savings_from_improved_driver_behaviour(mpg_percent_increase) {
        return (this.gallons_saved_per_year_from_route_optimization(mpg_percent_increase) * this.input.fuelCost);
    }
    total_annual_savings(idlingReductionFactor, routeOptimizationFactor, driverBehaviorIncreaseFactor) {
        return (this.annual_savings_from_idling_reduction(idlingReductionFactor) +
            this.annual_savings_from_route_optimization(routeOptimizationFactor) +
            this.annual_savings_from_improved_driver_behaviour(driverBehaviorIncreaseFactor));
    }
    total_gallons_saved(idlingReductionFactor, routeOptimizationFactor, driverBehaviorIncreaseFactor) {
        return (this.gallons_saved_per_year_from_idling_reduction(idlingReductionFactor) +
            this.gallons_saved_per_year_from_route_optimization(routeOptimizationFactor) +
            (this.total_gallons_of_gas_per_year(this.input.fuelEfficiency) -
                this.total_gallons_of_gas_per_year(this.increase_in_mpg_by(driverBehaviorIncreaseFactor))));
    }
}
exports.CalculatorService = CalculatorService;
